<template>
  <div
    class="min-h-screen bg-slate-50/60 font-sans text-slate-800 antialiased px-4 py-8 sm:px-6 lg:px-8"
  >
    <div class="max-w-7xl mx-auto">
      <!-- Header Section -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
      >
        <div>
          <div class="flex items-center gap-3">
            <div
              class="p-2.5 bg-blue-600 rounded-xl text-white shadow-md shadow-blue-500/20"
            >
              <CreditCard class="w-6 h-6" />
            </div>
            <div>
              <h1
                class="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
              >
                Smart Cards
              </h1>
              <p class="text-xs sm:text-sm text-slate-500 mt-0.5">
                Manage, issue, and inspect digital ID allocations
              </p>
            </div>
          </div>
        </div>

        <!-- Book Button -->
        <button
          @click="openBookingModal"
          :disabled="isBooking"
          class="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none text-white font-medium text-sm shadow-sm hover:shadow-md transition-all duration-150"
        >
          <CreditCard class="w-4 h-4" />
          <span>Book Smart Cards</span>
        </button>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center py-24 bg-white/60 backdrop-blur rounded-3xl border border-slate-100 shadow-sm"
      >
        <div
          class="w-10 h-10 border-3 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3"
        ></div>
        <p class="text-slate-500 font-medium text-sm">Loading smart cards...</p>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center py-20 px-4 bg-white rounded-3xl border border-red-100 shadow-sm text-center"
      >
        <div
          class="w-14 h-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mb-4"
        >
          <AlertCircle class="w-7 h-7" />
        </div>
        <h3 class="text-lg font-semibold text-slate-800 mb-1">
          Failed to Load Smart Cards
        </h3>
        <p class="text-slate-500 text-sm max-w-sm mb-6">
          {{ error }}
        </p>
        <button
          class="px-5 py-2.5 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors text-sm shadow-sm"
          @click="fetchData"
        >
          Try Again
        </button>
      </div>

      <!-- Main Content -->
      <template v-else>
        <!-- Quick Stats Overview -->
        <div
          class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5 mb-8"
        >
          <!-- Total -->
          <div
            class="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center gap-3.5"
          >
            <div
              class="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0"
            >
              <Users :size="20" />
            </div>
            <div class="min-w-0">
              <span
                class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900"
                >{{ stats.total }}</span
              >
              <span
                class="text-xs font-medium text-slate-400 uppercase tracking-wider"
                >Total</span
              >
            </div>
          </div>

          <!-- Active -->
          <div
            class="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center gap-3.5"
          >
            <div
              class="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0"
            >
              <CheckCircle :size="20" />
            </div>
            <div class="min-w-0">
              <span
                class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900"
                >{{ stats.active }}</span
              >
              <span
                class="text-xs font-medium text-slate-400 uppercase tracking-wider"
                >Active</span
              >
            </div>
          </div>

          <!-- In Transit -->
          <div
            class="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center gap-3.5"
          >
            <div
              class="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0"
            >
              <Truck :size="20" />
            </div>
            <div class="min-w-0">
              <span
                class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900"
                >{{ stats.inTransit }}</span
              >
              <span
                class="text-xs font-medium text-slate-400 uppercase tracking-wider"
                >Transit</span
              >
            </div>
          </div>

          <!-- Disabled -->
          <div
            class="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center gap-3.5"
          >
            <div
              class="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0"
            >
              <XCircle :size="20" />
            </div>
            <div class="min-w-0">
              <span
                class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900"
                >{{ stats.disabled }}</span
              >
              <span
                class="text-xs font-medium text-slate-400 uppercase tracking-wider"
                >Disabled</span
              >
            </div>
          </div>

          <!-- Roles Distribution -->
          <div
            class="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm col-span-2 sm:col-span-1 flex items-center justify-between"
          >
            <div class="flex items-center gap-2.5 min-w-0">
              <div
                class="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0"
              >
                <GraduationCap :size="17" />
              </div>
              <div class="min-w-0">
                <span
                  class="block text-base font-bold text-slate-900 leading-none"
                  >{{ stats.students }}</span
                >
                <span class="text-[11px] text-slate-400">Students</span>
              </div>
            </div>
            <div class="h-6 w-px bg-slate-200"></div>
            <div class="flex items-center gap-2.5 min-w-0">
              <div
                class="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0"
              >
                <UserCog :size="17" />
              </div>
              <div class="min-w-0">
                <span
                  class="block text-base font-bold text-slate-900 leading-none"
                  >{{ stats.teachers }}</span
                >
                <span class="text-[11px] text-slate-400">Teachers</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Filter & Search Controls -->
        <div
          class="bg-white rounded-2xl p-3 sm:p-4 border border-slate-100 shadow-sm mb-8"
        >
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-center"
          >
            <!-- Search Field -->
            <div class="relative lg:col-span-5">
              <Search
                class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none"
              />
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search name, ID, or email..."
                class="w-full py-2.5 pl-10 pr-4 text-sm bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 transition focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10"
              />
            </div>

            <!-- Class Filter (conditional) -->
            <div
              v-if="selectedRole === 'student'"
              class="lg:col-span-2"
            >
              <ClassDropdown @selected-class="onClassSelected" />
            </div>

            <!-- Status Filter -->
            <div
              :class="
                selectedRole === 'student' ? 'lg:col-span-2' : 'lg:col-span-3'
              "
            >
              <select
                v-model="selectedStatus"
                class="w-full py-2.5 px-3 text-sm bg-slate-50 border border-slate-200 rounded-xl text-slate-700 transition focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="inTransit">In Transit</option>
              </select>
            </div>

            <!-- Role Filter -->
            <div
              :class="
                selectedRole === 'student' ? 'lg:col-span-2' : 'lg:col-span-3'
              "
            >
              <select
                v-model="selectedRole"
                class="w-full py-2.5 px-3 text-sm bg-slate-50 border border-slate-200 rounded-xl text-slate-700 transition focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10"
              >
                <option value="">All Roles</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
              </select>
            </div>

            <!-- Clear Filter Button -->
            <div
              v-if="hasActiveFilters"
              class="lg:col-span-1"
            >
              <button
                @click="clearFilters"
                title="Reset Filters"
                class="w-full py-2.5 px-3 bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-600 rounded-xl text-xs font-semibold tracking-wide transition-colors flex items-center justify-center gap-1.5"
              >
                <XCircle class="w-3.5 h-3.5" />
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="smartCards.length === 0"
          class="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm"
        >
          <div
            class="w-16 h-16 rounded-2xl bg-slate-50 text-slate-300 flex items-center justify-center mx-auto mb-4"
          >
            <LayoutGrid
              class="w-8 h-8"
              :stroke-width="1.5"
            />
          </div>
          <h3 class="text-base font-semibold text-slate-800 mb-1">
            No Smart Cards Found
          </h3>
          <p class="text-sm text-slate-400 max-w-sm mx-auto">
            Try adjusting your search criteria or resetting filters to view all
            entries.
          </p>
        </div>

        <!-- Cards Grid -->
        <div
          v-else
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8"
        >
          <div
            v-for="card in smartCards"
            :key="card.smartCardId"
            class="group bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 shadow-sm hover:shadow-lg transition-all duration-200 flex flex-col overflow-hidden"
          >
            <!-- Top Status Bar -->
            <div
              class="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between"
            >
              <span
                :class="[
                  'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide uppercase',
                  card.status === 'active'
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20'
                    : card.status === 'disabled'
                    ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20'
                    : 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
                ]"
              >
                <span
                  :class="[
                    'w-1.5 h-1.5 rounded-full',
                    card.status === 'active'
                      ? 'bg-emerald-500 animate-pulse'
                      : card.status === 'disabled'
                      ? 'bg-rose-500'
                      : 'bg-amber-500',
                  ]"
                ></span>
                {{ formatStatus(card.status) }}
              </span>

              <span class="text-xs font-mono font-medium text-slate-400">
                #{{ card.smartCardId }}
              </span>
            </div>

            <!-- Profile Info Body -->
            <div class="p-5 flex-1 flex gap-4 items-start">
              <!-- Avatar Column -->
              <div class="relative flex-shrink-0">
                <div
                  class="w-16 h-16 rounded-2xl overflow-hidden ring-2 ring-slate-100 bg-slate-50 flex items-center justify-center"
                >
                  <img
                    v-if="card.photo && !imageErrors[card.smartCardId]"
                    :src="card.photo"
                    :alt="card.name"
                    class="w-full h-full object-cover"
                    @error="markImageError(card.smartCardId)"
                  />
                  <div
                    v-else
                    :style="{ backgroundColor: getAvatarColor(card.name) }"
                    class="w-full h-full flex items-center justify-center text-white font-bold text-xl uppercase"
                  >
                    {{ getInitial(card.name) }}
                  </div>
                </div>

                <!-- Role Pill -->
                <span
                  :class="[
                    'absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider shadow-sm text-white',
                    card.role === 'student' ? 'bg-blue-600' : 'bg-violet-600',
                  ]"
                >
                  {{ card.role }}
                </span>
              </div>

              <!-- Content Column -->
              <div class="min-w-0 flex-1">
                <h3
                  class="text-base font-semibold text-slate-900 capitalize truncate group-hover:text-blue-600 transition-colors"
                >
                  {{ card.name }}
                </h3>
                <p class="text-xs text-slate-400 truncate mb-3">
                  {{ card.schoolName }}
                </p>

                <div class="space-y-1.5 text-xs text-slate-600">
                  <div class="flex items-center gap-2 truncate">
                    <Mail class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span class="truncate">{{ card.email }}</span>
                  </div>

                  <div class="flex items-center gap-2 truncate">
                    <Phone class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span class="truncate">{{ card.phone }}</span>
                  </div>

                  <div
                    v-if="card.dob"
                    class="flex items-center gap-2 truncate"
                  >
                    <Calendar
                      class="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
                    />
                    <span>{{ formatDate(card.dob) }}</span>
                  </div>
                </div>

                <!-- Student Tags -->
                <div
                  v-if="card.role === 'student'"
                  class="flex gap-2 mt-3 flex-wrap"
                >
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded-md text-[11px] font-medium text-slate-600"
                  >
                    <BookOpen class="w-3 h-3 text-slate-400" />
                    {{ card.className }}
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded-md text-[11px] font-medium text-slate-600"
                  >
                    <Hash class="w-3 h-3 text-slate-400" />
                    {{ card.rollNumber }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Card Bottom Bar -->
            <div class="px-5 py-3 bg-slate-50/50 border-t border-slate-100">
              <button
                @click="viewCard(card)"
                class="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold bg-white hover:bg-blue-600 text-slate-700 hover:text-white border border-slate-200 hover:border-transparent transition-all shadow-sm"
              >
                <Eye class="w-3.5 h-3.5" />
                <span>View Profile</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div
          v-if="pagination && pagination.pages > 1"
          class="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200"
        >
          <span
            class="text-xs sm:text-sm text-slate-500 font-medium order-2 sm:order-1"
          >
            Showing page {{ pagination.page }} of {{ pagination.pages }} ({{
              pagination.total
            }}
            total)
          </span>

          <div class="inline-flex items-center gap-1.5 order-1 sm:order-2">
            <!-- First -->
            <button
              class="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition text-xs font-bold"
              :disabled="pagination.page === 1"
              @click="changePage(1)"
            >
              «
            </button>

            <!-- Prev -->
            <button
              class="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition flex items-center justify-center"
              :disabled="pagination.page === 1"
              @click="changePage(pagination.page - 1)"
            >
              <ChevronLeft class="w-4 h-4" />
            </button>

            <!-- Numbers -->
            <div class="hidden sm:flex gap-1">
              <template
                v-for="page in getVisiblePages()"
                :key="page"
              >
                <span
                  v-if="page === '...'"
                  class="w-8 h-8 flex items-center justify-center text-slate-400 text-xs"
                >
                  ...
                </span>
                <button
                  v-else
                  :class="[
                    'w-8 h-8 rounded-lg text-xs font-semibold transition',
                    page === pagination.page
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600',
                  ]"
                  @click="changePage(page)"
                >
                  {{ page }}
                </button>
              </template>
            </div>

            <!-- Next -->
            <button
              class="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition flex items-center justify-center"
              :disabled="pagination.page === pagination.pages"
              @click="changePage(pagination.page + 1)"
            >
              <ChevronRight class="w-4 h-4" />
            </button>

            <!-- Last -->
            <button
              class="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none transition text-xs font-bold"
              :disabled="pagination.page === pagination.pages"
              @click="changePage(pagination.pages)"
            >
              »
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Booking Type Modal -->
    <div
      v-if="showOptionsModal"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all"
      @click="closeOptionsModal"
    >
      <div
        class="bg-white rounded-3xl shadow-xl p-6 max-w-md w-full border border-slate-100"
        @click.stop
      >
        <div class="mb-5">
          <h2 class="text-xl font-bold text-slate-900">Select Booking Type</h2>
          <p class="text-xs sm:text-sm text-slate-500 mt-1">
            Choose which category of members to order smart cards for
          </p>
        </div>

        <div class="space-y-3">
          <button
            @click="selectBookingOption('teachers')"
            class="w-full text-left p-4 border border-slate-200 hover:border-violet-500 hover:bg-violet-50/50 rounded-2xl transition group flex items-start gap-3.5"
          >
            <div
              class="p-2 rounded-xl bg-violet-100 text-violet-600 group-hover:scale-105 transition-transform flex-shrink-0 mt-0.5"
            >
              <UserCog class="w-5 h-5" />
            </div>
            <div>
              <div class="font-semibold text-slate-900 text-sm">
                Book for Teachers
              </div>
              <div class="text-xs text-slate-500 mt-0.5">
                Generate and allocate cards for all staff & teachers
              </div>
            </div>
          </button>

          <button
            @click="selectBookingOption('students')"
            class="w-full text-left p-4 border border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 rounded-2xl transition group flex items-start gap-3.5"
          >
            <div
              class="p-2 rounded-xl bg-blue-100 text-blue-600 group-hover:scale-105 transition-transform flex-shrink-0 mt-0.5"
            >
              <GraduationCap class="w-5 h-5" />
            </div>
            <div>
              <div class="font-semibold text-slate-900 text-sm">
                Book for Students
              </div>
              <div class="text-xs text-slate-500 mt-0.5">
                Generate and batch cards for enrolled students
              </div>
            </div>
          </button>

          <button
            @click="selectBookingOption('both')"
            class="w-full text-left p-4 border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 rounded-2xl transition group flex items-start gap-3.5"
          >
            <div
              class="p-2 rounded-xl bg-emerald-100 text-emerald-600 group-hover:scale-105 transition-transform flex-shrink-0 mt-0.5"
            >
              <CreditCard class="w-5 h-5" />
            </div>
            <div>
              <div class="font-semibold text-slate-900 text-sm">
                Book for Everyone
              </div>
              <div class="text-xs text-slate-500 mt-0.5">
                Process complete allocation for both staff and students
              </div>
            </div>
          </button>
        </div>

        <button
          @click="closeOptionsModal"
          class="w-full mt-5 px-4 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition font-medium text-sm"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Booking Confirmation Modal -->
    <BookingConfirmationModal
      :is-open="showBookingModal"
      :booking-type="selectedOption"
      :is-loading="isBooking"
      @confirm="handleConfirmedBooking"
      @cancel="closeBookingModal"
    />
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import {
    CreditCard,
    Search,
    Users,
    CheckCircle,
    Truck,
    GraduationCap,
    UserCog,
    LayoutGrid,
    Mail,
    Phone,
    Calendar,
    BookOpen,
    Hash,
    Eye,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    XCircle,
  } from "lucide-vue-next";
  import { useRouter } from "vue-router";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import BookingConfirmationModal from "@/components/SmartCardAndDevice/ConfirmationModal.vue";
  import ClassDropdown from "@/components/SmartCardAndDevice/classFilter.vue";

  /* ================= STORE + TOAST ================= */
  const router = useRouter();
  const store = useStore();
  const { showToast } = useGlobalToast();

  /* ================= BOOKING MODALS ================= */
  const showOptionsModal = ref(false);
  const showBookingModal = ref(false);
  const selectedOption = ref("");
  const isBooking = ref(false);

  const openBookingModal = () => {
    showOptionsModal.value = true;
  };

  const closeOptionsModal = () => {
    showOptionsModal.value = false;
  };

  const selectBookingOption = (option) => {
    selectedOption.value = option;
    closeOptionsModal();
    showBookingModal.value = true;
  };

  const closeBookingModal = () => {
    showBookingModal.value = false;
    selectedOption.value = "";
  };

  const handleConfirmedBooking = async () => {
    isBooking.value = true;

    try {
      let payload = {};

      if (selectedOption.value === "students") payload.allStudents = true;
      else if (selectedOption.value === "teachers") payload.allTeachers = true;
      else if (selectedOption.value === "both") payload.both = true;

      await store.dispatch("bookSmartCard", payload);

      showToast(
        "success",
        `Smart Cards booked successfully for all ${selectedOption.value}!`,
      );

      await store.dispatch("fetchSmartCardStats");
      closeBookingModal();
      fetchData(1); // Reset to first page after booking
    } catch (err) {
      console.error(err);
      showToast("error", "Failed to book Smart Cards. Please try again.");
    } finally {
      isBooking.value = false;
    }
  };

  /* ================= FILTERS & SEARCH ================= */
  const searchQuery = ref("");
  const selectedRole = ref("");
  const imageErrors = ref({});

  const selectedStatus = ref("");
  const selectedClass = ref("");
  const loading = ref(false);
  const error = ref(null);
  let searchDebounceTimer = null;

  /* ================= COMPUTED ================= */
  const smartCards = computed(() => store.state.smartCards || []);
  const pagination = computed(() => store.state.cardsPagination);

  const hasActiveFilters = computed(() => {
    return (
      searchQuery.value ||
      selectedRole.value ||
      selectedStatus.value ||
      selectedClass.value
    );
  });

  const stats = computed(() => {
    const all = smartCards.value;
    return {
      total: all.length,
      active: all.filter((c) => c.status === "active").length,
      disabled: all.filter((c) => c.status === "disabled").length,
      inTransit: all.filter((c) => c.status === "inTransit").length,
      students: all.filter((c) => c.role === "student").length,
      teachers: all.filter((c) => c.role === "teacher").length,
    };
  });

  /* ================= METHODS ================= */

  /**
   * Fetch data from backend with pagination, search, and filters
   */
  const fetchData = async (page = 1) => {
    loading.value = true;
    error.value = null;

    try {
      const payload = {
        page,
        limit: 20,
        search: searchQuery.value,
        classId: selectedClass.value?._id || null,
        role: selectedRole.value || null,
        status: selectedStatus.value || null,
      };

      await store.dispatch("fetchAllSmartCards", payload);
    } catch (err) {
      error.value =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch smart cards";
      console.error("❌ Error fetching smart cards:", error.value);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Debounced search handler
   */
  const onSearchChange = () => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);

    searchDebounceTimer = setTimeout(() => {
      fetchData(1);
    }, 500);
  };

  /**
   * Handle class selection from dropdown
   */
  const onClassSelected = (cls) => {
    selectedClass.value = cls;
    fetchData(1);
  };

  /**
   * Handle status filter change
   */
  const handleStatusChange = () => {
    fetchData(1);
  };

  /**
   * Handle role filter change
   */
  const handleRoleChange = () => {
    fetchData(1);
  };

  /**
   * Change pagination page
   */
  const changePage = (page) => {
    if (page >= 1 && pagination.value && page <= pagination.value.pages) {
      fetchData(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /**
   * Get visible page numbers for pagination
   */
  const getVisiblePages = () => {
    if (!pagination.value) return [];

    const pages = [];
    const current = pagination.value.page;
    const total = pagination.value.pages;
    const delta = 2;

    for (
      let i = Math.max(1, current - delta);
      i <= Math.min(total, current + delta);
      i++
    ) {
      pages.push(i);
    }

    if (pages[0] > 1) {
      if (pages[0] > 2) pages.unshift("...");
      pages.unshift(1);
    }

    if (pages[pages.length - 1] < total) {
      if (pages[pages.length - 1] < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  /**
   * Clear all filters and search
   */
  const clearFilters = () => {
    searchQuery.value = "";
    selectedRole.value = "";
    selectedStatus.value = "";
    selectedClass.value = null;
    fetchData(1);
  };

  /**
   * View card details
   */
  const viewCard = async (card) => {
    if (card.role === "teacher") {
      router.push({
        name: "Teacher",
        query: {
          userId: card.userId,
        },
      });
    } else {
      await store.dispatch("fetchUserById", card.userId);
      router.push({
        name: "Students",
        query: {
          userId: card.userId,
        },
      });
    }
  };

  /**
   * Format date
   */
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /**
   * Format status for display
   */
  const formatStatus = (status) => {
    const statusMap = {
      active: "Active",
      disabled: "Disabled",
      inTransit: "In Transit",
    };
    return statusMap[status] || status;
  };

  /**
   * Handle image loading errors
   */
  const markImageError = (id) => {
    imageErrors.value[id] = true;
  };

  const getInitial = (name) => {
    if (!name) return "?";
    return name.trim().charAt(0).toUpperCase();
  };

  // Generate consistent color based on name
  const getAvatarColor = (name) => {
    const colors = [
      "#6366F1",
      "#0EA5E9",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  /* ================= WATCHERS ================= */
  watch(() => searchQuery.value, onSearchChange);
  watch(() => selectedStatus.value, handleStatusChange);
  watch(() => selectedRole.value, handleRoleChange);
  watch(selectedRole, (newRole) => {
    if (newRole !== "student") {
      onClassSelected("");
    }
  });

  /* ================= LIFECYCLE ================= */
  onMounted(() => {
    fetchData(1);
  });
</script>
